import { ThemeProvider } from "@emotion/react"
import { Box, CssBaseline, useMediaQuery, useTheme, Button } from "@mui/material"
import React, { useEffect, useState } from "react"
import { ThemeProvider as NewThemeProvider} from "@mui/material/styles"
import PropTypes from 'prop-types'
import SecurityService from "src/services/security-service"
import SessionTimeout from "src/utils/session-timeout"
import './scrollbar.css'
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import RefreshToken from "../../utils/refresh-token"
import SideBar, { HEIGHT_TOP_MENU } from "../../v2/components/SideBar/side-bar"
import { useDocumentTitle } from "../../v2/hooks/useDocumentTitle"
import theme from "../../utils/theme"
import newTheme from "../../v2/theme/theme"
import LoaderOverlay from "../../v2/components/LoaderOverlay/loader-overlay"
import Loader from "../../v2/components/Loader/loader"
import ChatbotInModal from "../../v2/components/Chatbot/chatbot-modal"
import { useLocation } from "react-router-dom"
import { CURRENT_PAGE_STORAGE_KEY } from "src/utils/constants"

const root = {
    display: "relative"
}
dayjs.extend(utc)

const LayoutPage = ({ children, title, loading }) => {
    const location = useLocation()

    useEffect(() => {
        if (location.pathname !== "/solicitar-acesso") {
            localStorage.setItem(CURRENT_PAGE_STORAGE_KEY, location.pathname)
        }    
    }, [location.pathname])

    const securityService = new SecurityService()
    const muiTheme = useTheme()
    const isMobile = useMediaQuery(muiTheme.breakpoints.down("lg"))

    const [isChatOpen, setIsChatOpen] = useState(false)

    useDocumentTitle(title)
    const body = document.body

    body.style.width = "100dvwd"
    body.style.height = "100dvhd"
    body.style.overflowX = "auto"

    useEffect(() => {
        window.scrollTo(0, 0)
        document.title = title
        securityService.logoutPassTime()
        securityService.logoutInvalidToken()
    }, [children, title, loading])

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    display: "flex",
                    gap: 3,
                    background: theme.palette.background.primary,
                    height: "100%",
                    width: "100%",
                    overflowX: "auto",
                }}
                style={root}>
                {/* Session Timeout */}
                <SessionTimeout />

                {/* Refresh Token Timeout */}
                <RefreshToken />

                {/* Theme Css */}
                <CssBaseline />
                {/* Side Menu and Page Content */}
                <SideBar />


                {/* Page Content */}
                <Box
                    component="main"
                    sx={{
                        position: 'relative',
                        flex: 1,
                        height: "100%",
                        mb: "5%",
                        pt: 2,
                        width: "100%",
                        maxWidth: "100%",
                        overflowX: "auto",
                        boxSizing: "border-box",
                        ml: {
                            lg: "233px",
                            md: "24px",
                            sm: "24px",
                            xxxs: "24px",
                        },
                        mr: {
                            lg: "24px",
                            md: "24px",
                            sm: "24px",
                            xxxs: "24px",
                        },
                        mt: isMobile ? HEIGHT_TOP_MENU : 0,
                    }}
                >
                    <Box
                        sx={{
                            position: { xs: 'relative', md: 'absolute' },
                            top: { md: 16 }, 
                            right: { md: 0 }, 
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mb: { xs: 2, md: 0 },
                            mt: { xs: 0, md: 1 },
                            width: { xs: '100%', md: 'auto' },
                            zIndex: 10
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={() => setIsChatOpen(true)}
                            sx={{
                                textTransform: 'uppercase',
                                px: 3,
                                py: 1,
                                backgroundColor: theme.palette.transparent.white10,
                                color: theme.palette.primary.main,        
                                borderColor: theme.palette.primary.main,
                                border: "1px solid",
                                '&:hover': {
                                    border: "2px solid",
                                    px: '23px',
                                    py: '7px',
                                }
                            }}
                        >
                            AJUDA
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: "100%",
                        }}
                    >
                        {children}
                    </Box>
                </Box>
            </Box>


            <NewThemeProvider theme={newTheme}>
                <ChatbotInModal
                    open={isChatOpen}
                    onClose={() => setIsChatOpen(false)}
                />
            </NewThemeProvider>

            {loading &&
                <LoaderOverlay>
                    <Loader />
                </LoaderOverlay>
            }
        </ThemeProvider>
    )
}

LayoutPage.propTypes = {
    children: PropTypes.any,
    title: PropTypes.string,
    loading: PropTypes.bool,
}


export default LayoutPage